import { useState } from 'react';

import { UseMutateAsyncFunction } from '@tanstack/react-query';
import { Action } from '../../../components/MultipleActionsInterfaces';
import {
  useBulkActivateCatalogs,
  useBulkAddClassificationPermissions,
  useBulkAddCustomers,
  useBulkAddSalesRepresentatives,
  useBulkDeactivateCatalogs,
  useBulkDeleteCatalogs,
} from '../../../services/modules/catalogs/catalogsBulk.queries';
import type { CatalogBulkBaseRequest } from '../../../services/modules/catalogs/catalogsBulk.repository.types';
import { Catalog } from '../catalog.types';
import {
  BulkActionModalKey,
  CatalogListMassActionType,
  UseCatalogListMassActionsParams,
  UseCatalogListMassActionsReturn,
} from './types/useCatalogListMassActions.types';

type ModalState = Record<BulkActionModalKey, boolean>;

const INITIAL_MODALS: ModalState = {
  activate: false,
  inactivate: false,
  remove: false,
  addCustomers: false,
  addReps: false,
  addClassifications: false,
};

const ACTION_TO_MODAL: Record<CatalogListMassActionType, BulkActionModalKey> = {
  activate_batch: 'activate',
  inactivate_batch: 'inactivate',
  remove_batch: 'remove',
  add_customers_batch: 'addCustomers',
  add_reps_batch: 'addReps',
  add_classifications_batch: 'addClassifications',
};

const useCatalogListMassActions = ({
  listItems,
  totalItems,
  reload,
  activeFilters,
  isMassActionFilter,
  selectedItems,
}: UseCatalogListMassActionsParams): UseCatalogListMassActionsReturn => {
  const activateMutation = useBulkActivateCatalogs();
  const deactivateMutation = useBulkDeactivateCatalogs();
  const deleteMutation = useBulkDeleteCatalogs();
  const addCustomersMutation = useBulkAddCustomers();
  const addRepsMutation = useBulkAddSalesRepresentatives();
  const addClassificationsMutation = useBulkAddClassificationPermissions();

  const [modals, setModals] = useState<ModalState>(INITIAL_MODALS);

  const closeModal = (key: BulkActionModalKey) => setModals((prev) => ({ ...prev, [key]: false }));

  const buildBaseRequest = () => ({
    isMassActionFilter,
    ...(isMassActionFilter ? { filter: activeFilters } : { catalogIds: [...selectedItems] }),
  });

  const executeBulkAction = async <T extends CatalogBulkBaseRequest>(
    mutation: { mutateAsync: UseMutateAsyncFunction<void, unknown, T> },
    modalKey: BulkActionModalKey,
    extraPayload?: Omit<T, keyof CatalogBulkBaseRequest>,
    onSuccess?: () => void,
  ) => {
    try {
      const request = (
        extraPayload ? { ...buildBaseRequest(), ...extraPayload } : buildBaseRequest()
      ) as T;
      await mutation.mutateAsync(request);
      closeModal(modalKey);
      onSuccess?.();
      reload();
    } catch {
      // error handled by mutation onError
    }
  };

  const handleMassAction = (action: Action<CatalogListMassActionType>) => {
    setModals((prev) => ({ ...prev, [ACTION_TO_MODAL[action.type]]: true }));
  };

  const handleBatchActivate = () => executeBulkAction(activateMutation, 'activate');

  const handleBatchInactivate = () => executeBulkAction(deactivateMutation, 'inactivate');

  const handleBatchRemove = () => executeBulkAction(deleteMutation, 'remove');

  const handleBatchAddCustomers = (customerIds: string[]) =>
    executeBulkAction(addCustomersMutation, 'addCustomers', { customerIds });

  const handleBatchAddReps = (salesRepresentativeIds: string[]) =>
    executeBulkAction(addRepsMutation, 'addReps', { salesRepresentativeIds });

  const handleBatchAddClassifications = (classificationIds: string[]) =>
    executeBulkAction(addClassificationsMutation, 'addClassifications', { classificationIds });

  const listItemsMap = new Map(listItems.map((c) => [c.id, c]));

  const selectedCatalogs = [...selectedItems]
    .map((id) => listItemsMap.get(id))
    .filter((c): c is Catalog => Boolean(c));

  const totalSelected = isMassActionFilter ? totalItems : selectedItems.size;

  const isAllSelected =
    isMassActionFilter || (listItems.length > 0 && selectedItems.size === listItems.length);

  const hasIsDefaultSelected = selectedCatalogs.some((c) => c.isDefault);

  const isActivateHidden =
    !isMassActionFilter && !hasIsDefaultSelected && selectedCatalogs.some((c) => c.active);

  const isInactivateHidden =
    !isMassActionFilter && !hasIsDefaultSelected && selectedCatalogs.some((c) => !c.active);

  const isRemoveHidden = !isMassActionFilter && selectedCatalogs.some((c) => c.isDefault);

  return {
    totalSelected,
    totalItems,
    isAllSelected,
    isActivateHidden,
    isInactivateHidden,
    isRemoveHidden,
    handleMassAction,
    isActivateModalOpen: modals.activate,
    closeActivateModal: () => closeModal('activate'),
    handleBatchActivate,
    isInactivateModalOpen: modals.inactivate,
    closeInactivateModal: () => closeModal('inactivate'),
    handleBatchInactivate,
    isRemoveModalOpen: modals.remove,
    closeRemoveModal: () => closeModal('remove'),
    handleBatchRemove,
    isAddCustomersModalOpen: modals.addCustomers,
    closeAddCustomersModal: () => closeModal('addCustomers'),
    handleBatchAddCustomers,
    isAddRepsModalOpen: modals.addReps,
    closeAddRepsModal: () => closeModal('addReps'),
    handleBatchAddReps,
    isAddClassificationsModalOpen: modals.addClassifications,
    closeAddClassificationsModal: () => closeModal('addClassifications'),
    handleBatchAddClassifications,
  };
};

export default useCatalogListMassActions;
